<template>
  <div class="lot" :class="'is-' + visualPhase" @click="unlockAudio">
    <video
      v-if="bgMode === 'video' && bgVideo"
      ref="bgVideo"
      class="lot__video"
      :src="bgVideo"
      autoplay
      muted
      loop
      playsinline
      preload="auto"
    />
    <div
      v-else-if="bgMode === 'image' && bgImage"
      class="lot__bg-img"
      :style="{ backgroundImage: 'url(' + bgImage + ')' }"
      aria-hidden="true"
    />
    <div v-else class="lot__fx" :class="'lot__fx--' + bgEffect" aria-hidden="true">
      <div class="lot__fx-layer lot__fx-base" />
      <div class="lot__fx-layer lot__fx-mid" />
      <div class="lot__fx-layer lot__fx-spark" />
    </div>
    <div class="lot__vignette" />

    <div class="lot__head">
      <div class="lot__banner">{{ confirmed ? '中奖名单' : '昵称抽奖' }}</div>
      <div v-if="prizeName" class="lot__prize-name">{{ prizeName }}</div>
    </div>

    <!-- 飞出胶囊 -->
    <div v-show="visualPhase === 'flying'" class="lot__fly">
      <div
        v-for="item in flyItems"
        :key="item.id"
        class="lot__chip lot__chip--fly"
        :class="'c' + item.tone"
        :style="item.style"
      >{{ item.nick }}</div>
    </div>

    <!-- 3D 球：外层公转；内层反向转，名字始终正对屏幕 -->
    <div
      v-show="visualPhase === 'idle' || visualPhase === 'sphere'"
      class="lot__persp"
      :class="{ 'is-fast': visualPhase === 'sphere' }"
    >
      <div class="lot__world">
        <div
          v-for="c in capsuleList"
          :key="c.id"
          class="lot__slot"
          :style="c.style"
        >
          <div class="lot__chip lot__chip--sphere" :class="'c' + c.tone">{{ c.nick }}</div>
        </div>
      </div>
    </div>

    <!-- 居中奖品预告 -->
    <div v-if="visualPhase === 'idle' && prizeImage" class="lot__prize">
      <img :src="prizeImage" alt="">
      <div class="lot__prize-title">{{ prizeName }}</div>
      <p>即将抽取</p>
    </div>
    <div v-else-if="visualPhase === 'idle'" class="lot__wait">
      <p>等待主持人抽取</p>
    </div>

    <!-- 揭晓 -->
    <div v-if="visualPhase === 'reveal' && winner" class="lot__reveal" :class="{ confirmed: confirmed }">
      <div class="lot__card">
        <img v-if="winner.avatar" :src="winner.avatar" alt="">
        <div class="lot__nick">{{ winner.nick || winner.uid }}</div>
        <p>{{ confirmed ? '恭喜中奖' : '开奖中…' }}</p>
        <div v-if="prizeName" class="lot__card-prize">{{ prizeName }}</div>
      </div>
    </div>

    <div class="lot__foot">已入场 {{ onlineCount || 0 }} 人</div>
    <div v-if="needGesture" class="lot__unlock">点击屏幕开启音效</div>
    <audio v-if="bgmUrl" ref="bgm" :src="bgmUrl" loop preload="auto" />
  </div>
</template>

<script>
import { getImageUrl } from '@/utils/image'

const CAPSULE_COUNT = 40
const FLY_MS = 2800
const SPHERE_MS = 2200
const TONES = 6
const FALLBACK_NICKS = ['嘉宾', '好运', '幸运星', '神秘嘉宾']

export default {
  name: 'PartyLotteryStage',
  props: {
    guests: { type: Array, default: () => [] },
    draft: { type: Object, default: null },
    h5: { type: Object, default: () => ({}) },
    onlineCount: { type: Number, default: 0 }
  },
  data() {
    return {
      visualPhase: 'idle',
      flyItems: [],
      flySeq: 0,
      needGesture: false,
      flyTimer: null,
      phaseTimer: null,
      lastDrawAt: 0,
      audioCtx: null,
      oscNodes: [],
      volRaf: 0,
      capsuleList: [],
      sphereRadius: 380
    }
  },
  computed: {
    useDefaultMedia() {
      return this.h5.useDefaultMedia !== false
    },
    bgMode() {
      const m = this.h5 && this.h5.bgMode
      if (m === 'image' || m === 'fx' || m === 'video') return m
      if (this.h5 && this.h5.bgVideo) return 'video'
      if (this.h5 && this.h5.bg) return 'image'
      return 'fx'
    },
    bgVideo() {
      return (this.h5 && this.h5.bgVideo) || ''
    },
    bgImage() {
      const raw = (this.h5 && this.h5.bg) || ''
      return raw ? getImageUrl(raw) : ''
    },
    bgEffect() {
      const e = (this.h5 && this.h5.bgEffect) || ''
      const ok = ['star_trail', 'warp', 'nebula', 'aurora', 'gold_bokeh', 'deep_blue']
      if (ok.indexOf(e) >= 0) return e
      return 'star_trail'
    },
    bgmUrl() {
      return (this.h5 && this.h5.bgm) || ''
    },
    prizeName() {
      return (this.draft && this.draft.prizeName) || ''
    },
    prizeImage() {
      return (this.draft && this.draft.prizeImage) || ''
    },
    winner() {
      return this.draft && this.draft.winner ? this.draft.winner : null
    },
    confirmed() {
      return this.draft && this.draft.status === 'confirmed'
    },
    nickPool() {
      const list = (this.guests || [])
        .map(g => {
          const n = g && (g.nick || g.nickName || g.nickname)
          if (n && String(n).trim()) return String(n).trim()
          if (g && g.uid) return String(g.uid)
          return ''
        })
        .filter(Boolean)
      // 有真实入场者只用真名（可重复铺满球）；无人时才用占位字
      return list.length ? list : FALLBACK_NICKS
    },
    nickPoolKey() {
      return this.nickPool.join('\u0001')
    }
  },
  watch: {
    draft: {
      deep: true,
      handler() {
        this.syncPhaseFromDraft()
      }
    },
    visualPhase() {
      this.syncBgmVolume()
    },
    nickPoolKey: {
      immediate: true,
      handler() {
        this.rebuildCapsules()
      }
    },
    sphereRadius() {
      this.rebuildCapsules()
    }
  },
  mounted() {
    this.refreshSphereRadius()
    this.rebuildCapsules()
    window.addEventListener('resize', this.onResize)
    this.syncPhaseFromDraft()
    this.tryPlayMedia()
    window.addEventListener('keydown', this.unlockAudio)
    window.addEventListener('click', this.unlockAudio)
  },
  beforeDestroy() {
    cancelAnimationFrame(this.volRaf)
    clearInterval(this.flyTimer)
    clearTimeout(this.phaseTimer)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('keydown', this.unlockAudio)
    window.removeEventListener('click', this.unlockAudio)
    this.stopPad()
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      try { this.audioCtx.close() } catch (e) { /* ignore */ }
    }
  },
  methods: {
    onResize() {
      this.refreshSphereRadius()
    },
    refreshSphereRadius() {
      const w = window.innerWidth || 1920
      const h = window.innerHeight || 1080
      const next = Math.round(Math.min(w, h) * 0.36)
      if (next !== this.sphereRadius) this.sphereRadius = next
    },
    rebuildCapsules() {
      const pool = this.nickPool
      const n = CAPSULE_COUNT
      const R = this.sphereRadius
      const out = []
      for (let i = 0; i < n; i++) {
        const y = 1 - (i / (n - 1)) * 2
        const radius = Math.sqrt(Math.max(0, 1 - y * y))
        const theta = Math.PI * (3 - Math.sqrt(5)) * i
        const x = Math.cos(theta) * radius
        const z = Math.sin(theta) * radius
        out.push({
          id: 'c' + i,
          nick: pool[i % pool.length],
          tone: (i % TONES) + 1,
          style: {
            transform: 'translate3d(' + (x * R) + 'px,' + (y * R) + 'px,' + (z * R) + 'px)'
          }
        })
      }
      this.capsuleList = out
    },
    syncPhaseFromDraft() {
      const d = this.draft || {}
      const status = d.status || ''
      if (status === 'confirmed' && d.winner) {
        this.stopFlying()
        this.visualPhase = 'reveal'
        return
      }
      if (status === 'pending' && d.winner) {
        const drawnAt = Number(d.drawnAt || 0)
        if (drawnAt && drawnAt === this.lastDrawAt && this.visualPhase === 'reveal') return
        if (drawnAt !== this.lastDrawAt) {
          this.lastDrawAt = drawnAt
          this.beginDrawSequence()
        }
        return
      }
      this.stopFlying()
      this.visualPhase = 'idle'
    },
    beginDrawSequence() {
      this.stopFlying()
      this.visualPhase = 'flying'
      this.flyItems = []
      this.flyTimer = setInterval(this.spawnFly, 110)
      this.phaseTimer = setTimeout(() => {
        this.stopFlying()
        this.visualPhase = 'sphere'
        this.phaseTimer = setTimeout(() => {
          this.visualPhase = 'reveal'
        }, SPHERE_MS)
      }, FLY_MS)
    },
    stopFlying() {
      clearInterval(this.flyTimer)
      this.flyTimer = null
      this.flyItems = []
    },
    spawnFly() {
      const pool = this.nickPool
      const nick = pool[Math.floor(Math.random() * pool.length)]
      const id = ++this.flySeq
      const ang = Math.random() * Math.PI * 2
      const dist = 280 + Math.random() * 520
      const tx = Math.cos(ang) * dist
      const ty = Math.sin(ang) * dist * 0.55
      const rot = (Math.random() * 50 - 25).toFixed(1)
      const delay = 0
      this.flyItems.push({
        id,
        nick,
        tone: (id % TONES) + 1,
        style: {
          '--tx': tx + 'px',
          '--ty': ty + 'px',
          '--rot': rot + 'deg',
          animationDelay: delay + 'ms'
        }
      })
      if (this.flyItems.length > 36) this.flyItems.shift()
    },
    unlockAudio() {
      this.needGesture = false
      this.tryPlayMedia()
    },
    tryPlayMedia() {
      this.$nextTick(() => {
        const v = this.$refs.bgVideo
        if (v && v.paused) {
          const p = v.play()
          if (p && p.catch) p.catch(() => {})
        }
        if (this.bgmUrl) {
          const a = this.$refs.bgm
          if (a) {
            // 等待抽奖：30%；抽奖中由 syncBgmVolume 1 秒渐变到 100%
            if (this.visualPhase === 'idle') a.volume = 0.3
            else a.volume = Math.max(a.volume, 0.3)
            const p = a.play()
            if (p && p.catch) {
              p.catch(() => { this.needGesture = true })
            }
            this.syncBgmVolume()
          }
          this.stopPad()
          return
        }
        if (this.useDefaultMedia) {
          this.startPad()
        }
      })
    },
    /** 待机 30%；抽奖中（flying/sphere/reveal）1 秒渐变到 100% */
    isDrawingPhase() {
      return this.visualPhase === 'flying' || this.visualPhase === 'sphere' || this.visualPhase === 'reveal'
    },
    syncBgmVolume() {
      const target = this.isDrawingPhase() ? 1 : 0.3
      this.fadeHtmlBgmTo(target, 1000)
      this.fadePadGainTo(this.isDrawingPhase() ? 0.12 : 0.04, 1)
    },
    fadeHtmlBgmTo(target, durationMs) {
      const a = this.$refs.bgm
      if (!a) return
      cancelAnimationFrame(this.volRaf)
      const from = typeof a.volume === 'number' ? a.volume : 0.3
      if (Math.abs(from - target) < 0.01) {
        a.volume = target
        return
      }
      const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now()
      const step = (now) => {
        const p = Math.min(1, (now - t0) / durationMs)
        // ease-out 轻微加速到目标
        const k = 1 - Math.pow(1 - p, 1.5)
        a.volume = Math.max(0, Math.min(1, from + (target - from) * k))
        if (p < 1) this.volRaf = requestAnimationFrame(step)
      }
      this.volRaf = requestAnimationFrame(step)
    },
    fadePadGainTo(target, durationSec) {
      const gain = this.oscNodes && this.oscNodes.gain
      if (!gain || !this.audioCtx) return
      try {
        const t = this.audioCtx.currentTime
        gain.gain.cancelScheduledValues(t)
        gain.gain.setValueAtTime(gain.gain.value, t)
        gain.gain.linearRampToValueAtTime(target, t + durationSec)
      } catch (e) { /* ignore */ }
    },
    startPad() {
      if (this.oscNodes && this.oscNodes.started) {
        this.syncBgmVolume()
        return
      }
      const AC = window.AudioContext || window.webkitAudioContext
      if (!AC) return
      try {
        if (!this.audioCtx) this.audioCtx = new AC()
        if (this.audioCtx.state === 'suspended') {
          this.audioCtx.resume().catch(() => { this.needGesture = true })
        }
        const ctx = this.audioCtx
        const gain = ctx.createGain()
        gain.gain.value = 0.04
        gain.connect(ctx.destination)
        const freqs = [196, 247, 294]
        const oscs = freqs.map((f, i) => {
          const o = ctx.createOscillator()
          o.type = i === 1 ? 'triangle' : 'sine'
          o.frequency.value = f
          const g = ctx.createGain()
          g.gain.value = 0.35
          o.connect(g)
          g.connect(gain)
          o.start()
          return o
        })
        this.oscNodes = { oscs, gain, started: true }
        this.syncBgmVolume()
      } catch (e) {
        this.needGesture = true
      }
    },
    stopPad() {
      const nodes = this.oscNodes
      if (!nodes || !nodes.oscs) return
      nodes.oscs.forEach(o => {
        try { o.stop() } catch (e) { /* ignore */ }
      })
      this.oscNodes = []
    }
  }
}
</script>

<style scoped>
.lot {
  position: absolute;
  inset: 0;
  overflow: hidden;
  color: #fff;
}
.lot__video,
.lot__bg-img,
.lot__fx,
.lot__vignette {
  position: absolute;
  inset: 0;
}
.lot__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}
.lot__bg-img {
  z-index: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.lot__fx {
  z-index: 0;
  overflow: hidden;
  background: #0a0418;
}
.lot__fx-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.lot__fx--star_trail .lot__fx-base {
  background:
    radial-gradient(circle at 50% 50%, #3a1a7a 0%, #0a0418 78%),
    repeating-radial-gradient(circle at 50% 50%, rgba(180, 120, 255, 0.2) 0 2px, transparent 2px 36px);
  animation: lotPulse 5s ease-in-out infinite;
}
.lot__fx--star_trail .lot__fx-mid {
  inset: -40%;
  background: conic-gradient(from 0deg, transparent 0 70%, rgba(140, 80, 255, 0.22) 80%, transparent 100%);
  /* 慢旋即可，快旋 conic 很吃 GPU */
  animation: lotSpin 22s linear infinite;
}
.lot__fx--warp .lot__fx-base {
  background: radial-gradient(circle at 50% 50%, #2a1058 0%, #05010f 88%);
}
.lot__fx--warp .lot__fx-mid {
  inset: -35%;
  background: repeating-conic-gradient(from 0deg at 50% 50%,
    rgba(160, 100, 255, 0) 0deg 7deg,
    rgba(190, 130, 255, 0.38) 7deg 8.5deg,
    transparent 8.5deg 16deg);
  animation: lotSpin 18s linear infinite;
  opacity: 0.88;
}
.lot__fx--warp .lot__fx-spark {
  /* 去掉高频 filter/点阵动画，减轻卡顿 */
  background: radial-gradient(ellipse at center, transparent 52%, rgba(0, 0, 0, 0.28) 100%);
}
.lot__fx--nebula .lot__fx-base {
  background:
    radial-gradient(ellipse at 30% 40%, rgba(255, 80, 180, 0.45) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 60%, rgba(120, 80, 255, 0.5) 0%, transparent 55%),
    radial-gradient(circle at 50% 50%, #1a0830 0%, #050010 100%);
  animation: lotPulse 6s ease-in-out infinite;
}
.lot__fx--nebula .lot__fx-mid {
  background: radial-gradient(circle at 60% 30%, rgba(255, 200, 255, 0.2) 0%, transparent 40%);
  animation: lotDrift 10s ease-in-out infinite alternate;
}
.lot__fx--aurora .lot__fx-base {
  background: linear-gradient(180deg, #041018 0%, #0a2030 50%, #051018 100%);
}
.lot__fx--aurora .lot__fx-mid {
  background:
    linear-gradient(105deg, transparent 20%, rgba(40, 255, 180, 0.35) 40%, transparent 55%),
    linear-gradient(80deg, transparent 35%, rgba(80, 160, 255, 0.3) 55%, transparent 70%);
  background-size: 200% 100%;
  animation: lotAurora 10s ease-in-out infinite alternate;
  /* blur 全屏层开销大，去掉 */
}
.lot__fx--gold_bokeh .lot__fx-base {
  background: radial-gradient(circle at 50% 40%, #3a2810 0%, #120a04 70%);
}
.lot__fx--gold_bokeh .lot__fx-mid {
  background-image:
    radial-gradient(circle at 20% 30%, rgba(255, 200, 80, 0.45) 0 12%, transparent 13%),
    radial-gradient(circle at 70% 25%, rgba(255, 160, 60, 0.35) 0 8%, transparent 9%),
    radial-gradient(circle at 40% 70%, rgba(255, 220, 120, 0.3) 0 10%, transparent 11%),
    radial-gradient(circle at 80% 65%, rgba(255, 180, 80, 0.4) 0 14%, transparent 15%);
  animation: lotPulse 4s ease-in-out infinite;
}
.lot__fx--deep_blue .lot__fx-base {
  background:
    radial-gradient(circle at 50% 50%, #123060 0%, #020814 65%),
    repeating-radial-gradient(circle at 50% 50%, rgba(100, 180, 255, 0.12) 0 2px, transparent 2px 32px);
  animation: lotPulse 5.5s ease-in-out infinite;
}
.lot__fx--deep_blue .lot__fx-mid {
  inset: -55%;
  background: conic-gradient(from 90deg, transparent 0 65%, rgba(80, 160, 255, 0.28) 78%, transparent 100%);
  animation: lotSpin 12s linear infinite;
}
@keyframes lotPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.88; }
}
@keyframes lotSpin {
  to { transform: rotate(360deg); }
}
@keyframes lotDrift {
  from { transform: translate(-3%, -2%) scale(1); }
  to { transform: translate(3%, 2%) scale(1.05); }
}
@keyframes lotAurora {
  from { background-position: 0% 0%, 100% 0%; }
  to { background-position: 100% 0%, 0% 0%; }
}
.lot__vignette {
  z-index: 1;
  background: radial-gradient(ellipse at center, transparent 58%, rgba(8, 0, 20, 0.4) 100%);
  pointer-events: none;
}
.lot__head {
  position: relative;
  z-index: 5;
  padding-top: 28px;
  text-align: center;
}
.lot__banner {
  display: inline-block;
  padding: 8px 36px;
  border-radius: 24px;
  background: linear-gradient(90deg, #7b3cff, #c84bff);
  font-size: 28px;
  letter-spacing: 4px;
  box-shadow: 0 0 24px rgba(160, 80, 255, 0.55);
}
.lot__prize-name {
  margin-top: 10px;
  font-size: 20px;
  opacity: 0.85;
}
.lot__persp {
  --lot-spin: 16s;
  position: absolute;
  inset: 0;
  z-index: 2;
  perspective: 1200px;
  pointer-events: none;
  overflow: visible;
}
.lot__persp.is-fast {
  --lot-spin: 5.5s;
}
.lot__world {
  position: absolute;
  left: 50%;
  top: 52%;
  width: 0;
  height: 0;
  transform-style: preserve-3d;
  animation: lotWorldSpin var(--lot-spin) linear infinite;
}
/* 槽位只负责球面上的位置 */
.lot__slot {
  position: absolute;
  left: 0;
  top: 0;
  transform-style: preserve-3d;
}
/* 与公转等速反向，抵消自转，文字始终朝向镜头 */
.lot__chip--sphere {
  animation: lotFaceCounter var(--lot-spin) linear infinite;
}
@keyframes lotWorldSpin {
  from { transform: rotateX(14deg) rotateY(0deg); }
  to { transform: rotateX(14deg) rotateY(360deg); }
}
@keyframes lotFaceCounter {
  from { transform: rotateY(0deg) rotateX(-14deg); }
  to { transform: rotateY(-360deg) rotateX(-14deg); }
}
.lot__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
.lot__fly {
  position: absolute;
  inset: 0;
  z-index: 3;
  overflow: hidden;
  pointer-events: none;
}
.lot__chip--fly {
  position: absolute;
  left: 50%;
  top: 50%;
  animation: lotFly 1.45s cubic-bezier(0.22, 0.7, 0.3, 1) forwards;
}
.c1 { background: linear-gradient(90deg, #ff5f6d, #ffc371); }
.c2 { background: linear-gradient(90deg, #7b2ff7, #f107a3); }
.c3 { background: linear-gradient(90deg, #00c6ff, #0072ff); }
.c4 { background: linear-gradient(90deg, #11998e, #38ef7d); }
.c5 { background: linear-gradient(90deg, #f7971e, #ffd200); }
.c6 { background: linear-gradient(90deg, #c471f5, #fa71cd); }
@keyframes lotFly {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.15) rotate(0); }
  18% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(var(--rot)); }
  100% { opacity: 0; transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(1.15) rotate(var(--rot)); }
}
.lot__prize,
.lot__wait,
.lot__reveal {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 6;
  text-align: center;
}
.lot__prize img {
  width: 280px;
  height: 280px;
  object-fit: contain;
  filter: drop-shadow(0 12px 40px rgba(180, 80, 255, 0.45));
}
.lot__prize-title {
  margin-top: 12px;
  font-size: 36px;
  font-weight: 700;
}
.lot__prize p,
.lot__wait p {
  opacity: 0.75;
  font-size: 22px;
}
.lot__wait p {
  font-size: 22px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.75);
}
.lot__card {
  min-width: 360px;
  padding: 28px 36px 32px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(90, 40, 160, 0.92), rgba(28, 10, 70, 0.92));
  border: 3px solid #c9a6ff;
  box-shadow: 0 0 40px rgba(180, 90, 255, 0.5);
}
.lot__card img {
  width: 220px;
  height: 220px;
  object-fit: cover;
  border-radius: 18px;
  margin-bottom: 12px;
}
.lot__nick {
  font-size: 48px;
  font-weight: 800;
}
.lot.is-reveal .lot__nick {
  text-shadow: 0 0 18px rgba(255, 220, 120, 0.55);
}
.lot__card-prize {
  margin-top: 8px;
  opacity: 0.8;
}
.lot__foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 18px;
  z-index: 5;
  text-align: center;
  opacity: 0.55;
  font-size: 16px;
}
.lot__unlock {
  position: absolute;
  right: 24px;
  bottom: 18px;
  z-index: 8;
  font-size: 14px;
  opacity: 0.7;
}
</style>
